import FocusTrap from 'focus-trap-react';
import PropTypes from 'prop-types';
import {
  createElement, useEffect, useLayoutEffect, useRef,
} from 'react';
import {createUseStyles} from 'react-jss';
import {connect} from 'react-redux';
import NavigationType from '../../../enums/NavigationType';
import {hideOverlay} from '../../actions/overlay-actions';
import getShouldPauseFocusTrap from '../../selectors/OverlaySelectors/getShouldPauseFocusTrap';
import {getSettings} from '../../selectors/selectors';
import doMetric from '../../tracking/bigQueryMetricRecorder';
import Track from '../../utils/tracking';
import {getOverlay} from './OverlayLibrary';

const useStyles = createUseStyles({
  '@keyframes fadeIn': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
  checkoutOverlayProvider: {
    position: 'fixed',
    zIndex: 200,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
    animationName: '$fadeIn',
    animationDuration: '0.3s',
    background: 'rgba(0, 0, 0, 0.6)',
  },
});

export function CheckoutOverlayProvider(props) {
  const classes = useStyles();
  const prevSelectedOverlayRef = useRef();
  
  useLayoutEffect(() => {
    prevSelectedOverlayRef.current = props.overlay.selectedOverlay;
  });

  const prevSelectedOverlay = prevSelectedOverlayRef.current;
  
  useEffect(() => {
    if (!prevSelectedOverlay && props.overlay.selectedOverlay) {
      try {
        doMetric('pushstate', [{name: 'reason', value: 'Entered Overlay'}]);
      } catch (error) {
        Track.track(error);
      }
      window.history.pushState({reason: NavigationType.OVERLAY}, '');
    }
    if (prevSelectedOverlay && !props.overlay.selectedOverlay && window.history.state
      && window.history?.state?.reason === NavigationType.OVERLAY) {
      window.history.back();
    }
  }, [prevSelectedOverlay]);
  
  if (!props.overlay.selectedOverlay) return null;
  const overlay = getOverlay(props.overlay);
  
  if (props.removeFocusTrap && props.overlay.selectedOverlay === 'COLLECTION') {
    return (
      <div
        className={classes.checkoutOverlayProvider}
        data-test="CheckoutOverlayProvider"
        role="dialog" // Role and tabIndex required for focus trap
        tabIndex="-1"
      >
        {createElement(overlay.component, overlay.props)}
      </div>
    );
  }
  
  return (
    <FocusTrap
      focusTrapOptions={{
        // Focus Trap will focus on the first tabbable element. If there are no tabbable items it will match this
        // selector as long as that element itself is focussable (i.e. tab index == -1).
        // All 'popups' will work by default. It's only OrderProcessingOverlay that has no tabbable items.
        fallbackFocus: '#overlayLabel__id',
      }}
      paused={props.shouldPauseFocusTrap}
    >
      <div
        className={classes.checkoutOverlayProvider}
        data-test="CheckoutOverlayProvider"
        role="dialog" // Role and tabIndex required for focus trap
        tabIndex="-1"
      >
        {createElement(overlay.component, overlay.props)}
      </div>
    </FocusTrap>
  );
}

CheckoutOverlayProvider.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  overlay: PropTypes.object.isRequired,
  removeFocusTrap: PropTypes.bool.isRequired,
  shouldPauseFocusTrap: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  overlay: state.overlay,
  removeFocusTrap: getSettings(state)?.remove_focus_trap,
  shouldPauseFocusTrap: !!getShouldPauseFocusTrap(state),
});

export default connect(
  mapStateToProps,
  {hideOverlay},
)(CheckoutOverlayProvider);
